import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Wrench,
  FileText,
  PenTool,
  Check,
  RefreshCw,
  Signature,
  ShieldAlert,
  ClipboardList,
  Eye,
  X,
} from "lucide-react";
import { useSignoffInbox } from "@/hooks/useSignoffInbox";
import { useAuth } from "@/context/AuthContext";

const KIND_ICON = {
  inspection: FileText,
  breakdown: Wrench,
  ppf: PenTool,
  hazard: ShieldAlert,
  handover: ClipboardList,
};

const KIND_TONE = {
  inspection: { bg: "rgba(120, 180, 255, 0.14)", fg: "#8ab6ff" },
  breakdown:  { bg: "rgba(240, 100, 100, 0.14)", fg: "#ff9b9b" },
  ppf:        { bg: "rgba(245, 130, 32, 0.18)",  fg: "#ffb066" },
  hazard:     { bg: "rgba(240, 100, 100, 0.14)", fg: "#ff9b9b" },
  handover:   { bg: "rgba(148, 163, 184, 0.14)", fg: "#a8b3c1" },
};

/**
 * NotificationCenter — the SIGN-OFF INBOX.
 * Shows only items awaiting sign-off from the current user's role. Clicking a
 * row deep-links straight into that resource with the sign-off panel already
 * open (`?open={id}&signoff=1`).
 */
export default function NotificationCenter() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);
  const navigate = useNavigate();

  const { items, unreadCount, alertCount, canSupervise, markAllRead, dismiss, refresh, loading } = useSignoffInbox({ user });
  // Total "in the tray" count = sign-offs + informational alerts. The
  // sign-off (`unreadCount`) is what drives the orange pulse — informational
  // alerts still show a badge but stay muted (grey) so the supervisor isn't
  // told "you have a pending sign-off" when nothing needs signing.
  const badgeCount = unreadCount + alertCount;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Non-supervisors don't sign anything, so hide the bell entirely.
  if (!canSupervise) return null;

  const onToggle = () => {
    const willOpen = !open;
    setOpen(willOpen);
    // Clear the badge INSTANTLY on open — the previous 1.2s `setTimeout`
    // was the "still shows 5 open after opening" bug Josh reported.
    if (willOpen && (unreadCount > 0 || alertCount > 0)) markAllRead();
  };

  const go = (n) => {
    // Informational alerts (freshly-lodged handovers) are heads-up only —
    // once the supervisor taps VIEW, they've been "seen" and we drop the
    // notification so it doesn't linger in the tray.
    if (n.informational) dismiss(n.id);
    setOpen(false);
    // Deep-link into the resource with sign-off pre-opened.
    navigate(`${n.route}?open=${encodeURIComponent(n.rid)}&signoff=1`);
  };

  // Show every non-dismissed item in the tray, not just the unread ones.
  // Previously we sliced `unread` here — so once `markAllRead()` fired
  // (1.2s after opening), the whole list emptied. Now the badge count and
  // the item list are decoupled: opening drops the badge, but every
  // pending sign-off stays visible until it's signed OR explicitly ×-ed.
  const visible = items.slice(0, 30);

  return (
    <div className="relative" data-testid="notification-center">
      <button
        ref={btnRef}
        className="btn btn-sm btn-ghost relative"
        onClick={onToggle}
        title="Pending sign-offs"
        data-testid="notif-toggle"
        aria-expanded={open}
        style={{ overflow: "visible" }}
      >
        <Signature className="w-4 h-4" />
        <span className="hidden md:inline">Sign-offs</span>
        {badgeCount > 0 && (
          <span
            className={`absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1.5 rounded-full flex items-center justify-center text-[10px] font-black data-num pointer-events-none${unreadCount > 0 ? " sl-badge-pulse" : ""}`}
            style={{
              background: unreadCount > 0 ? "var(--primary)" : "rgba(148,163,184,0.6)",
              color: unreadCount > 0 ? "#0a0a0a" : "#0a0a0a",
              border: "2px solid var(--surface-1, #0e0e0e)",
              lineHeight: 1,
              zIndex: 2,
            }}
            data-testid="notif-badge"
            title={unreadCount > 0
              ? `${unreadCount} pending sign-off${unreadCount === 1 ? "" : "s"}${alertCount ? ` + ${alertCount} alert${alertCount === 1 ? "" : "s"}` : ""}`
              : `${alertCount} alert${alertCount === 1 ? "" : "s"} — no sign-offs pending`}
          >
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute right-0 top-[calc(100%+8px)] w-[400px] max-w-[92vw] overflow-hidden z-50"
          style={{
            // Josh (Feb 26): the sign-off tray was hard to read against the
            // page behind it — swap the translucent panel-glass for a solid
            // dark surface with a subtle blur for depth. Every row is now
            // legible regardless of what's underneath.
            background: "rgba(10, 10, 10, 0.98)",
            backdropFilter: "blur(24px) saturate(1.2)",
            WebkitBackdropFilter: "blur(24px) saturate(1.2)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 16,
            boxShadow: "0 30px 80px -20px rgba(0,0,0,0.95), 0 20px 40px -15px rgba(245,130,32,0.22)",
          }}
          data-testid="notif-popover"
        >
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.35)" }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Signature className="w-4 h-4 shrink-0" style={{ color: "var(--primary)" }} />
              <div className="font-display font-black uppercase tracking-widest text-sm truncate">
                Sign-offs &amp; alerts
              </div>
              {visible.length > 0 && (
                <span className="pill pill-orange !text-[10px] !py-0.5 !px-1.5 ml-1 shrink-0">
                  {visible.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={refresh}
                className="text-[11px] uppercase tracking-widest text-muted hover:text-white px-2 py-1 flex items-center gap-1"
                title="Refresh"
                data-testid="notif-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button
                onClick={markAllRead}
                className="text-[11px] uppercase tracking-widest text-muted hover:text-white px-2 py-1 flex items-center gap-1"
                data-testid="notif-mark-read"
              >
                <Check className="w-3.5 h-3.5" /> Read
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-auto">
            {visible.length === 0 ? (
              <div className="p-8 text-center">
                <div
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <Bell className="w-5 h-5 text-muted" />
                </div>
                <div className="text-sm text-muted">Nothing needs your attention right now.</div>
                <div className="text-[11px] text-muted mt-1">You&apos;re fully caught up.</div>
              </div>
            ) : (
              <ul>
                {visible.map((n) => {
                  const Icon = KIND_ICON[n.kind] || FileText;
                  const tone = KIND_TONE[n.kind] || KIND_TONE.inspection;
                  return (
                    <li key={n.id} className="relative group" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                      <button
                        onClick={() => go(n)}
                        className="w-full text-left px-4 py-3 pr-11 flex items-start gap-3 hover:bg-[rgba(255,255,255,0.03)] transition-colors"
                        data-testid={`notif-item-${n.id}`}
                      >
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.bg}` }}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className="text-[10px] uppercase tracking-widest font-black"
                              style={{ color: tone.fg }}
                            >
                              {n.label}
                            </span>
                            {n.priority && (
                              <span
                                className="text-[10px] uppercase tracking-widest font-black"
                                style={{ color: "var(--muted)" }}
                              >
                                · {String(n.priority)}
                              </span>
                            )}
                          </div>
                          <div className="font-display font-black text-sm mt-0.5 leading-snug">
                            {n.subject}
                          </div>
                          <div className="text-[11px] text-muted mt-0.5 truncate">
                            by {n.actor} · {relative(n.ts)}
                          </div>
                        </div>
                        <div
                          className={n.informational ? "pill !text-[10px] !py-0.5 !px-2 shrink-0 mt-0.5" : "pill pill-orange !text-[10px] !py-0.5 !px-2 shrink-0 mt-0.5"}
                          title={n.verb}
                          style={n.informational
                            ? { background: "rgba(148, 163, 184, 0.14)", color: "#a8b3c1", border: "1px solid rgba(148, 163, 184, 0.3)" }
                            : undefined}
                        >
                          {n.informational ? <Eye className="w-3 h-3" /> : <PenTool className="w-3 h-3" />}
                          {n.informational ? "View" : "Sign"}
                        </div>
                      </button>
                      {/* Per-item dismiss — hides this row from the tray. The
                          record itself stays intact (the prod API doesn't
                          expose a DELETE for these). */}
                      <button
                        onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                        className="absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[rgba(255,255,255,0.08)] transition-opacity"
                        style={{ color: "var(--muted)" }}
                        data-testid={`notif-dismiss-${n.id}`}
                        title="Dismiss this alert"
                        aria-label="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted flex items-center justify-between"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            <span>Auto-refresh 20s</span>
            <span>Site · Live</span>
          </div>
        </div>
      )}
    </div>
  );
}

function relative(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}
